import { BookOpen, Expand, Film, Info, Pause, Play, RotateCcw, Shrink, Volume2, VolumeX, Waves } from "lucide-react";
import { SCENARIOS, STAGES } from "../story";

const SPEEDS = [0.5, 1, 1.5, 2];

const Btn = ({ on, label, children, onClick, testid, active }) => (
  <button type="button" onClick={onClick} aria-label={label} title={label} aria-pressed={active} data-testid={testid}
    className={`cine-btn ${active ? "is-active" : ""}`}>{children}{on && <span className="cine-btn-label">{on}</span>}</button>
);

export function StageNav({ tl, labels }) {
  const prog = (s) => {
    const bs = tl.beats.filter((b) => b.stage === s.id);
    if (!bs.length) return 0;
    const a = bs[0].start, z = bs[bs.length - 1].start + bs[bs.length - 1].dur;
    return Math.max(0, Math.min(1, (tl.t - a) / (z - a)));
  };
  return (
    <nav className="cine-stages" aria-label="Stages" data-testid="cinema-stage-nav">
      {STAGES.map((s, i) => {
        const inPlay = tl.stagesInPlay.has(s.id), now = tl.stageIdx === i;
        return (
          <button key={s.id} type="button" disabled={!inPlay} onClick={() => tl.seekStage(s.id)} data-testid={`stage-nav-${s.id}`}
            className={`cine-stage ${now ? "is-now" : ""} ${!inPlay ? "is-skipped" : ""}`} aria-current={now ? "step" : undefined}>
            <span className="cine-mono text-[10px] opacity-60">{String(i + 1).padStart(2, "0")}</span>
            <span className="cine-stage-name">{labels?.[i] || s.label}</span>
            {!inPlay && <span className="text-[9px] text-rose-300/80 cine-mono">SKIPPED</span>}
            <span className="cine-stage-bar"><span style={{ width: `${prog(s) * 100}%` }} /></span>
          </button>
        );
      })}
    </nav>
  );
}

export function Controls({ tl, ui, setUi, onFullscreen, isFs, showScenarios = true }) {
  return (
    <div className="cine-controls" data-testid="cinema-controls">
      <div className="flex items-center gap-1.5">
        <Btn label={tl.playing ? "Pause" : "Play"} onClick={tl.toggle} testid="cinema-play-toggle">{tl.playing ? <Pause size={16} /> : <Play size={16} />}</Btn>
        <Btn label="Restart" onClick={tl.restart} testid="cinema-restart"><RotateCcw size={16} /></Btn>
        <div className="cine-seg" role="group" aria-label="Playback speed">
          {SPEEDS.map((s) => <button key={s} type="button" onClick={() => tl.setSpeed(s)} className={tl.speed === s ? "is-on" : ""} data-testid={`cinema-speed-${s}`}>{s}×</button>)}
        </div>
      </div>
      <div className="cine-seg cine-scen" role="group" aria-label="Scenario" style={showScenarios ? undefined : { display: "none" }}>
        {Object.values(SCENARIOS).map((s) => (
          <button key={s.id} type="button" onClick={() => tl.chooseScenario(s.id)} className={tl.scenarioId === s.id ? "is-on" : ""} data-testid={`cinema-scenario-${s.id}`} title={s.blurb}>{s.label}</button>
        ))}
      </div>
      <div className="flex items-center gap-1.5">
        <Btn label={ui.sound ? "Sound is on, turn off" : "Sound is off, turn on"} onClick={() => setUi({ sound: !ui.sound })} testid="cinema-sound-toggle" active={ui.sound}>{ui.sound ? <Volume2 size={16} /> : <VolumeX size={16} />}</Btn>
        <Btn label="Reduced motion" onClick={() => setUi({ reduced: !ui.reduced })} testid="cinema-reduced-toggle" active={ui.reduced}><Waves size={16} /></Btn>
        <Btn label="Cinema view" onClick={() => setUi({ cinema: !ui.cinema })} testid="cinema-view-toggle" active={ui.cinema}><Film size={16} /></Btn>
        <Btn label="Decision journal" onClick={() => setUi({ sheet: "journal" })} testid="cinema-journal-btn"><BookOpen size={16} /></Btn>
        <Btn label="What is simulated" onClick={() => setUi({ sheet: "about" })} testid="cinema-about-btn"><Info size={16} /></Btn>
        <Btn label={isFs ? "Exit fullscreen" : "Fullscreen"} onClick={onFullscreen} testid="cinema-fullscreen">{isFs ? <Shrink size={16} /> : <Expand size={16} />}</Btn>
      </div>
    </div>
  );
}
